import { toastNotifyOptionsDefault } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { Toast, type ToastVariant } from '@shinetools/lumen-react'
import React from 'react'
import { toast, type ToastOptions } from 'react-toastify'

import { NotificationKeys } from '../../enums/notificationKeys'
import i18n from '../../utils/i18n'

export const LUMEN_TOAST_CLASS_NAME = 'lumen-toast'

const NOTIFICATION_AUTO_CLOSE_MS_MINIMUM = 2000
const NOTIFICATION_AUTO_CLOSE_MS_MAXIMUM = 7000
const AVERAGE_CHARACTER_READING_SPEED = 75
const ERROR_MESSAGE_EXTRA_TIME = 1000

export interface NotifyLumenToastProps {
  id: NotificationKeys
  message: string
  variant?: ToastVariant
}

const getAutoCloseTime = (message: string, variant: ToastVariant): number => {
  const messageLength = message.length

  const dynamicTimeBasedOnMessage = Math.min(
    Math.max(messageLength * AVERAGE_CHARACTER_READING_SPEED, NOTIFICATION_AUTO_CLOSE_MS_MINIMUM),
    NOTIFICATION_AUTO_CLOSE_MS_MAXIMUM,
  )

  if (variant === 'danger') {
    return Math.min(dynamicTimeBasedOnMessage + ERROR_MESSAGE_EXTRA_TIME, NOTIFICATION_AUTO_CLOSE_MS_MAXIMUM)
  }

  return dynamicTimeBasedOnMessage
}

export const notifyLumenToast = (
  { id, message, variant = 'default' }: NotifyLumenToastProps,
  toastOptions?: ToastOptions,
): void => {
  if (!id || !message) {
    trackError('You have to pass "id" and "message" for the notification to make it work')
    return
  }

  const cleanedMessage = message.replace(/^\d\d\d - /, '')
  const renderToast = ({ closeToast }: { closeToast?: () => void }) => (
    <Toast
      dismissLabel={i18n.t('close')}
      duration={0}
      message={cleanedMessage}
      onDismiss={() => closeToast?.()}
      variant={variant}
    />
  )
  const options: ToastOptions = {
    ...toastNotifyOptionsDefault,
    autoClose: getAutoCloseTime(cleanedMessage, variant),
    className: LUMEN_TOAST_CLASS_NAME,
    ...toastOptions,
    toastId: id,
  }

  if (toast.isActive(id)) {
    toast.update(id, { ...options, render: renderToast })
    return
  }

  toast(renderToast, options)
}
